import React, { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { ASSETS } from "../../components/Decorative";
import { LogOut, Settings, Clock, IceCream, BookOpen, Image as ImageIcon, ExternalLink, Sparkles, Snowflake, Megaphone } from "lucide-react";
import SiteInfoTab from "./tabs/SiteInfoTab";
import OpeningHoursTab from "./tabs/OpeningHoursTab";
import TodaysIceTab from "./tabs/TodaysIceTab";
import MenuTab from "./tabs/MenuTab";
import GalleryTab from "./tabs/GalleryTab";
import FlavorsTab from "./tabs/FlavorsTab";
import TakeHomeTab from "./tabs/TakeHomeTab";
import PromotionsTab from "./tabs/PromotionsTab";

const tabs = [
    { id: "info", label: "Über Uns & Kontakt", icon: Settings, component: SiteInfoTab },
    { id: "hours", label: "Öffnungszeiten", icon: Clock, component: OpeningHoursTab },
    { id: "flavors", label: "Sorten", icon: Sparkles, component: FlavorsTab },
    { id: "today", label: "Heutiges Softeis", icon: IceCream, component: TodaysIceTab },
    { id: "takehome", label: "Für Später", icon: Snowflake, component: TakeHomeTab },
    { id: "promos", label: "Aktionen", icon: Megaphone, component: PromotionsTab },
    { id: "menu", label: "Unser Angebot", icon: BookOpen, component: MenuTab },
    { id: "gallery", label: "Galerie", icon: ImageIcon, component: GalleryTab },
];

const AdminDashboard = () => {
    const { user, loading, logout } = useAuth();
    const [active, setActive] = useState("info");

    if (loading) return null;
    if (!user) return <Navigate to="/admin/login" replace />;

    const ActiveComponent = tabs.find((t) => t.id === active)?.component;

    return (
        <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
            <header className="bg-white border-b border-cream-dark sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src={ASSETS.mascot} alt="" className="w-10 h-10" />
                        <div>
                            <div className="font-serif text-lg text-deep-green leading-tight">Softeis Garten</div>
                            <div className="text-xs text-deep-green/60">Admin-Dashboard</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Link to="/" target="_blank" className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-deep-green hover:bg-cream-dark transition" data-testid="view-site-link">
                            <ExternalLink className="w-4 h-4" /> Webseite ansehen
                        </Link>
                        <button
                            onClick={logout}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm bg-cream-dark hover:bg-pastel-pink text-deep-green transition"
                            data-testid="admin-logout-button"
                        >
                            <LogOut className="w-4 h-4" /> Abmelden
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 grid lg:grid-cols-[240px_1fr] gap-8">
                <aside className="lg:sticky lg:top-24 lg:self-start">
                    <nav className="flex lg:flex-col gap-1 overflow-x-auto pb-2 lg:pb-0" data-testid="admin-tabs">
                        {tabs.map((t) => {
                            const Icon = t.icon;
                            return (
                                <button
                                    key={t.id}
                                    onClick={() => setActive(t.id)}
                                    data-testid={`admin-tab-${t.id}`}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium whitespace-nowrap transition ${
                                        active === t.id
                                            ? "bg-garden-green text-white shadow-md"
                                            : "text-deep-green hover:bg-cream-dark"
                                    }`}
                                >
                                    <Icon className="w-4 h-4" />
                                    {t.label}
                                </button>
                            );
                        })}
                    </nav>
                </aside>

                <main className="min-w-0">
                    <h2 className="font-serif text-2xl text-deep-green mb-6">
                        {tabs.find((t) => t.id === active)?.label}
                    </h2>
                    {ActiveComponent && <ActiveComponent />}
                </main>
            </div>
        </div>
    );
};

export default AdminDashboard;
